import { Link, useLocation } from '@tanstack/react-router';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { SIGN_IN_PATH, signInHref, useSession } from '@/lib/session';

/**
 * The shell's 404 — `FE-04.11`, `entry-surfaces.md` D10, completed by
 * `FE-06.1`.
 *
 * `GET /boards/:id` (and every other read route) answers 404 for **both**
 * "does not exist" and "exists, but not yours to see" — deliberately, so
 * the route is not an existence oracle (`apps/server/README.md` §11,
 * `CLAUDE.md`: "A caller outside a read scope gets 404, never 401 or
 * 403"). This component must not pretend to know which one happened, and
 * the copy below names neither cause.
 *
 * ---------------------------------------------------------------------------
 * D10'S SESSION-CONDITIONAL ACTION, WHICH THIS FILE WAS LEFT HALF-BUILT FOR
 * ---------------------------------------------------------------------------
 * D10 asks for **Sign in** with no session and **Back to your boards** with
 * one. `FE-04.11` shipped only the second, because that chunk fetched nothing
 * and D2's own warning is exactly the failure a wrong guess produces —
 * telling an already-signed-in member to sign in again. `D1` gives the shell
 * a session (`useSession`, `GET /me`), so the swap is now a read rather than
 * a guess.
 *
 * **Three states, not two, and the third is the one D10 does not mention.**
 * While the probe is in flight we do not know, and "we do not know yet" must
 * not render either branch (D12's rule, applied here). So pending renders the
 * session-*independent* action — `Back to your boards`, whose destination `/`
 * is correct for both a member (their boards) and a stranger (`FE-06.1`'s
 * redirect sends them to sign in from there). Nothing flashes, and no visitor
 * is ever told the wrong thing; the only cost is that a genuinely signed-out
 * visitor sees the generic action for the fraction of a second before the
 * probe answers.
 *
 * `isSignedOut` — `isUnauthenticated(error)`, not `!profile` — is what
 * selects the Sign in branch, for the same reason every other branch in this
 * chunk uses it: an expired session keeps its last-good `profile` beside the
 * new 401, and `!profile` would call that signed in.
 *
 * ⚠ **The `next` points back at the address that 404'd, and that is correct.**
 * It looks wrong — why return someone to a page that was not found? — but
 * "not found" here also means "not yours to see", and the whole reason D10
 * offers Sign in at all is that signing in may make the address resolve. A
 * `next` of `/` would silently discard the one thing the visitor was trying
 * to reach.
 *
 * A test that renders this component needs a `QueryClientProvider`
 * (`test/query.tsx`) — that is the shell fetching, and it is `D1`'s ripple.
 */
export function RouteNotFound() {
  const { t } = useInterfaceLanguage();
  const location = useLocation();
  const { isSignedOut } = useSession();

  /*
   * ⚠ `{ pathname, searchStr }`, never `location.href`. `href` carries the
   * origin, and `safeNextPath` — which reads this value back on the way out —
   * rejects anything not beginning with a single `/`, so a `next` built from
   * `href` would collapse the redirect to `/` with nothing red anywhere.
   * `entry-surfaces.md` D2's worked example gets this wrong.
   *
   * The one address that must not be its own `next` is `/sign-in`, which
   * cannot 404 — it is a registered route — but the guard costs one
   * comparison and stops a nested `?next=%2Fsign-in%3Fnext%3D…` from ever
   * being constructible here.
   */
  const signInTarget =
    location.pathname === SIGN_IN_PATH
      ? SIGN_IN_PATH
      : signInHref({ pathname: location.pathname, search: location.searchStr });

  return (
    <div className="mx-auto max-w-md py-16 text-center">
      <h1 className="text-xl font-semibold text-ink">{t('route.notFound.title')}</h1>
      <p className="mt-2 text-ink-muted">{t('route.notFound.body')}</p>
      <p className="mt-6">
        {isSignedOut ? (
          /*
           * A plain anchor rather than a `Link`: `signInHref` owns the one
           * spelling of this URL, and splitting it back into `to` + `search`
           * would be a second place the `next` key is written. The cost is a
           * document load for a visitor whose cache is empty anyway.
           */
          <a
            href={signInTarget}
            className="font-medium text-accent underline underline-offset-2"
          >
            {t('route.notFound.signIn')}
          </a>
        ) : (
          <Link to="/" className="font-medium text-accent underline underline-offset-2">
            {t('route.notFound.backToBoards')}
          </Link>
        )}
      </p>
    </div>
  );
}
